import { RequestAbortedError } from 'errors';

const parseHeaders = (rawHeaders: string) => {
  const headers = new Headers();
  // Replace instances of \r\n and \n followed by at least one space or horizontal tab with a space
  // https://tools.ietf.org/html/rfc7230#section-3.2
  const preProcessedHeaders = rawHeaders.replace(/\r?\n[\t ]+/g, ' ');
  preProcessedHeaders.split(/\r?\n/).forEach((line: any) => {
    const parts = line.split(':');
    const key = parts.shift().trim();
    if (key) {
      const value = parts.join(':').trim();
      headers.append(key, value);
    }
  });
  return headers;
};

const uploadFetch = <T extends FetchOptions>(url: string, options: T) =>
  new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const opts: any = {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders() || ''),
      };
      opts.url = 'responseURL' in xhr ? xhr.responseURL : opts.headers.get('X-Request-URL');
      const body = 'response' in xhr ? xhr.response : (xhr as any).responseText;
      resolve(new Response(body, opts));
    };
    xhr.onerror = () => {
      reject(new TypeError('Network request failed'));
    };
    xhr.ontimeout = () => {
      reject(new TypeError('Network request failed'));
    };

    xhr.open(options.method, url, true);

    Object.keys(options.headers).forEach((key) => {
      xhr.setRequestHeader(key, options.headers[key]);
    });

    if (xhr.upload) {
      xhr.upload.onprogress = options.onProgress?.bind(xhr) ?? (() => {});
    }

    options.setAbortTriggerCallback?.(() => {
      xhr.abort();
      xhr.upload.onprogress = null;
      reject(new RequestAbortedError());
    });

    xhr.send(options.body);
  });

interface FetchOptions {
  onProgress?: (event: ProgressEvent) => void;
  body: any;
  setAbortTriggerCallback: (callback: () => void) => void;
  headers: any;
  upload: any;
  method: string;
}

const customFetch = <T extends FetchOptions>(uri: string, options: T) => {
  if (options.onProgress) {
    // file upload
    return uploadFetch(uri, options);
  }
  return fetch(uri, options);
};

export default customFetch;
